import React, { useCallback, useEffect, useRef, useState } from 'react';
import Button from '../Button/Button';
import Banner from '../Banner/Banner';
import SectionPliable from '../SectionPliable/SectionPliable';
import { ReactComponent as PdfIcon } from '../../assets/icons/pdf.svg';
import './TabMessagerie.css';

// Composant de messagerie partage MS + BDC. Le transport est parametre par `apiBase`
// (prefixe d'endpoint via Core) : `/subsequents` (defaut, MS) ou `/bdc` (BDC). Les endpoints
// `${apiBase}/operation/{id}/messagerie[...]` ont la meme forme dans les deux modules.
// Le gating d'envoi (`operationStatus >= 7` = prestataire retenu / commande validee) est
// commun aux deux : le statut 7 designe l'etat "retenu/valide" en MS comme en BDC. Le canal
// Q&R (seuils de consultation `>= 2`) n'est utilise que par MS (BDC passe channel='messagerie').
// La prop `userRole` a ete retiree car elle etait inerte (jamais lue) et laissait croire
// a tort que l'affichage s'adaptait au role.

// ── Types ─────────────────────────────────────────────────────────────────────

interface Message {
  id: number;
  auteur_type: 'acheteur' | 'prestataire';
  auteur_id_entite?: number | null;
  question_num?: number | null;
  auteur_nom?: string | null;
  auteur_prenom?: string | null;
  auteur_entreprise?: string | null;
  contenu: string;
  channel: string;
  created_at: string;
  is_mine?: boolean;
}

interface TabMessagerieProps {
  operationId:     number;
  operationStatus: number;
  channel:         'questions_reponses' | 'messagerie';
  // Prefixe d'endpoint via Core : '/subsequents' (defaut, MS) ou '/bdc' (BDC).
  apiBase?:        string;
  onMarkRead?:     (channel: 'questions_reponses' | 'messagerie') => void;
  // CTA de navigation vers l'onglet suivant (typiquement un <NextTabButton />), injecte par le
  // parent. Generique et opt-in : le libelle et la condition d'affichage appartiennent au module
  // appelant, la messagerie ne fait que lui donner sa place dans la barre d'actions.
  // Remplace l'ancien `onEvaluerPrestataire` (libelle fige cote socle) : les 4 modules V2 passent
  // desormais ce prop, et plus aucun appelant ne demandait le chemin historique.
  nextTabButton?: React.ReactNode;
  // Date d'activation de la messagerie operationnelle (= signature du marche), injectee par le
  // parent quand le marche est signe. Definie -> bandeaux d'etat affiches en tete du canal
  // messagerie. Non definie (autres contextes) -> aucun bandeau. Chaine vide/null -> active sans date.
  messagerieActiveSince?: string | null;
  // Date de fin de consultation (= date limite de remise), injectee par le parent pour le canal
  // Questions/reponses. Affichee dans le bandeau vert « consultation terminee ». Optionnelle :
  // absente -> bandeau vert sans date.
  consultationEndedSince?: string | null;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const MAX_CHARS = 2000;

const formatDate = (iso: string): string => {
  const d = new Date(iso);
  return (
    d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' }) +
    ' à ' +
    d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
  );
};

const getAuteurLabel = (msg: Message, channel: 'questions_reponses' | 'messagerie'): string => {
  if (channel === 'questions_reponses') {
    if (msg.auteur_type === 'acheteur') return 'Acheteur';
    const prefix = `Prestataire #${msg.auteur_id_entite ?? '?'}`;
    return msg.question_num ? `${prefix} — Question ${msg.question_num}` : prefix;
  }
  // Messagerie privée : affichage complet
  const nom = [msg.auteur_prenom, msg.auteur_nom].filter(Boolean).join(' ');
  const entreprise = msg.auteur_entreprise ? ` — ${msg.auteur_entreprise}` : '';
  return nom ? `${nom}${entreprise}` : (msg.auteur_type === 'acheteur' ? 'Acheteur' : 'Prestataire');
};

// ── Composant ─────────────────────────────────────────────────────────────────

const TabMessagerie: React.FC<TabMessagerieProps> = ({ operationId, operationStatus, channel, apiBase = '/subsequents', onMarkRead, nextTabButton, messagerieActiveSince, consultationEndedSince }) => {
  const apiUrl  = process.env.REACT_APP_API_URL;
  const headers: HeadersInit = {
    Authorization: `Bearer ${localStorage.getItem('token')}`,
    'Content-Type': 'application/json',
  };

  const [messages, setMessages]   = useState<Message[]>([]);
  const [loading, setLoading]     = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending]     = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);

  const threadEndRef = useRef<HTMLDivElement>(null);
  const threadRef = useRef<HTMLDivElement>(null);

  // Impression du fil de discussion (clone du thread dans une fenetre dediee).
  const handlePrintDiscussion = () => {
    const node = threadRef.current;
    if (!node) return;
    const titre = channel === 'questions_reponses' ? 'Questions et réponses' : 'Messagerie opérationnelle';
    const styles = Array.from(document.querySelectorAll("link[rel='stylesheet'], style"))
      .map((n) => n.outerHTML).join('\n');
    const win = window.open('', '_blank');
    if (!win) return;
    win.document.open();
    win.document.write(`<html lang="fr"><head><meta charset="UTF-8" /><title>${titre}</title>${styles}
      <style>@page{margin:12mm} body{font-family:Arial,sans-serif;background:#fff;margin:0;padding:16px}
      .msg-thread{max-height:none !important;overflow:visible !important}
      .no-print{display:none !important} h1{font-size:1.6rem;color:#595959;margin:0 0 16px}</style>
      </head><body><h1>${titre}</h1>${node.outerHTML}</body></html>`);
    win.document.close();
    win.onload = () => { win.focus(); win.print(); setTimeout(() => win.close(), 500); };
  };

  const consultationLancee = operationStatus >= 2;
  // Statut operation MS : 2 = consultation en cours (echanges Q&R ouverts) ; > 2 = consultation
  // passee (analyse et au-dela) -> Q&R en lecture seule. Le 3 est un statut commande, pas operation.
  const consultationEnCours = operationStatus === 2;
  const consultationTerminee = operationStatus > 2;
  const prestataireChoisi  = operationStatus >= 7;

  const peutEnvoyer =
    channel === 'questions_reponses'
      ? consultationEnCours
      : prestataireChoisi;

  // ── Chargement ───────────────────────────────────────────────────────────────

  const loadMessages = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const res = await fetch(
        `${apiUrl}${apiBase}/operation/${operationId}/messagerie?channel=${channel}`,
        { headers }
      );
      if (!res.ok) throw new Error(`Erreur ${res.status}`);
      const json = await res.json();
      setMessages(json.data ?? []);
    } catch (err: any) {
      setLoadError(err.message);
    } finally {
      setLoading(false);
    }
  }, [operationId, channel]); // eslint-disable-line react-hooks/exhaustive-deps

  const markRead = useCallback(async () => {
    try {
      const res = await fetch(
        `${apiUrl}${apiBase}/operation/${operationId}/messagerie/mark-read`,
        { method: 'POST', headers, body: JSON.stringify({ channel }) }
      );
      const data = await res.json().catch(() => null);
      if (!res.ok || data?.status !== 'success') {
        return; // Ne pas effacer le badge si le backend n'a pas confirmé la lecture
      }
      onMarkRead?.(channel);
    } catch {
      // Silencieux — non-critique
    }
  }, [operationId, channel, onMarkRead]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    loadMessages().then(() => markRead());
  }, [loadMessages, markRead]);

  useEffect(() => {
    threadEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // ── Envoi ────────────────────────────────────────────────────────────────────

  const handleSubmit = async () => {
    const contenu = newMessage.trim();
    if (!contenu || sending) return;
    setSending(true);
    setSendError(null);
    try {
      const res = await fetch(
        `${apiUrl}${apiBase}/operation/${operationId}/messagerie`,
        { method: 'POST', headers, body: JSON.stringify({ contenu, channel }) }
      );
      if (!res.ok) {
        if (res.status === 429) {
          const retryAfter = res.headers.get('Retry-After');
          const delay = retryAfter ? `dans ${retryAfter} seconde(s)` : 'dans quelques instants';
          throw new Error(`Trop de messages envoyés. Réessayez ${delay}.`);
        }
        const body = await res.json().catch(() => null);
        throw new Error(body?.message ?? `Erreur ${res.status}`);
      }
      setNewMessage('');
      await loadMessages();
    } catch (err: any) {
      setSendError(err.message);
    } finally {
      setSending(false);
    }
  };

  // ── Rendu ─────────────────────────────────────────────────────────────────────

  // Bouton "Imprimer la discussion" : a gauche de la barre d'actions d'envoi (a gauche du
  // compteur). Si l'envoi est impossible (Q&R fermee, pas de barre), rendu seul en pied.
  const printButton = (
    <Button
      value="Imprimer la discussion"
      icon={<PdfIcon />}
      variant="secondary"
      className="no-print"
      onClick={handlePrintDiscussion}
      disabled={messages.length === 0}
    />
  );

  // CTA de navigation vers l'onglet suivant, a droite de l'impression. Rendu uniquement si le
  // parent l'a injecte : la messagerie ne decide pas s'il y a une suite, elle lui prete sa barre.
  // Un seul CTA de navigation dans la barre, et son libelle appartient a l'appelant.
  const nextTabAction = nextTabButton ?? null;

  return (
    <>
      {/* Bannières d'état : AU-DESSUS de la section (hors du flux du thread, sinon décalage). */}
      {channel === 'messagerie' && messagerieActiveSince !== undefined && (
        <Banner
          variant="success"
          filled
          message={`La messagerie opérationnelle horodatée est active entre le prestataire et l'acheteur${
            messagerieActiveSince ? ` depuis le ${messagerieActiveSince}` : ''
          }.`}
        />
      )}

      {channel === 'questions_reponses' && !consultationLancee && (
        <div className="msg-info-banner">
          La consultation n'est pas encore lancée. Vous pourrez envoyer des messages une fois la consultation démarrée.
        </div>
      )}

      {channel === 'questions_reponses' && consultationTerminee && (
        <Banner
          variant="success"
          filled
          message={`La consultation est terminée${
            consultationEndedSince ? ` depuis le ${consultationEndedSince}` : ''
          }. Il n'est donc plus possible d'échanger dans cet onglet.`}
        />
      )}

      {channel === 'messagerie' && !prestataireChoisi && (
        <div className="msg-info-banner">
          La messagerie privée sera disponible une fois que l'acheteur aura sélectionné un prestataire.
        </div>
      )}

      {/* SectionPliable : MEME entete, meme pli et meme zone blanche que les sections des autres
          onglets (Consultation, Offre...). L'onglet portait auparavant un entete maison
          (`.msg-section-header`, 2rem sans chevron) qui detonnait avec le reste de l'application. */}
      <SectionPliable
        title={channel === 'questions_reponses' ? 'Questions et réponses' : 'Messagerie opérationnelle'}
        defaultOpen
        className="msg-section"
      >

        {/* Regle du canal : DANS la section, sous son en-tete — c'est une consigne de redaction, elle
            appartient a l'espace ou l'on ecrit, pas au bandeau d'etat au-dessus.
            Affichee en PERMANENCE : conditionnee a l'etat du canal, elle etait invisible avant son
            ouverture et apres sa cloture, alors qu'il faut la connaitre AVANT d'ecrire.
            Variante `info` (grise) et non `warning` : c'est une regle du canal, pas une alerte.
            Meme traitement sur les deux canaux, et donc identique cote acheteur et prestataire, sur
            BC comme sur MS — l'onglet est ce composant unique. */}
        {channel === 'questions_reponses' && (
          <Banner
            variant="info"
            className="msg-regle-canal"
            message={
              "Pendant la consultation, il est possible d'échanger dans cet onglet. Les messages seront lus par " +
              "tous les candidats, ils doivent donc être anonymes et ne pas mentionner d'éléments qui permettraient " +
              "de vous identifier (à l'exception de l'acheteur)."
            }
          />
        )}

        {channel === 'messagerie' && (
          <Banner
            variant="info"
            className="msg-regle-canal"
            message={
              "L'acheteur et le prestataire retenu peuvent échanger librement dans cet espace sécurisé. " +
              'Les Ordres de Services (OS) peuvent également être notifiés via cette messagerie. Pour tout ' +
              "document nécessaire, n'hésitez pas à utiliser les liens URL vers des plateformes telles que " +
              '« France Transfert ».'
            }
          />
        )}

        <div className="msg-thread" ref={threadRef}>
        {loading && (
          <div className="msg-loading">
            <div className="spinner" />
            <span>Chargement des messages...</span>
          </div>
        )}

        {loadError && <div className="msg-error">Impossible de charger les messages.</div>}

        {!loading && !loadError && messages.length === 0 && (
          <div className="msg-empty">Aucun message pour le moment.</div>
        )}

        {!loading && !loadError && messages.map((msg) => {
          // is_mine calculé côté backend (auteur_id_entite du viewer vs auteur_id_entite du message)
          const isMine = msg.is_mine === true;
          return (
            <div
              key={msg.id}
              className={`msg-bubble ${isMine ? 'msg-bubble--mine' : ''} msg-bubble--${msg.auteur_type}`}
            >
              <div className="msg-bubble-header">
                <span className="msg-bubble-author">
                  {getAuteurLabel(msg, channel)}
                </span>
                <span className="msg-bubble-date">{formatDate(msg.created_at)}</span>
              </div>
              <div className="msg-bubble-body">{msg.contenu}</div>
            </div>
          );
        })}

        <div ref={threadEndRef} />
      </div>

      {/* Zone de redaction : elle reste DANS la section, avec le fil qu'elle alimente. « Envoyer »
          n'est pas une action d'onglet, c'est le geste attache a ce champ. */}
      {peutEnvoyer && (
        <div className="msg-input-bar">
          <textarea
            className="msg-input-textarea"
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value.slice(0, MAX_CHARS))}
            placeholder="Votre message..."
            maxLength={MAX_CHARS}
            disabled={sending}
            rows={2}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSubmit();
              }
            }}
          />
          <div className="msg-input-actions">
            <span className="msg-char-count">{newMessage.length} / {MAX_CHARS}</span>
            {sendError && <span className="msg-send-error">{sendError}</span>}
            <Button
              onClick={handleSubmit}
              value={sending ? 'Envoi...' : 'Envoyer'}
              disabled={sending || newMessage.trim().length === 0}
            />
          </div>
        </div>
      )}
      </SectionPliable>

      {/* Boutons d'ACTION de l'onglet (impression, bascule vers l'onglet suivant) : HORS de la
          section et en bas de page, comme dans tous les autres onglets. Ils vivaient dans la barre
          d'envoi — donc a l'interieur de la section, et ils disparaissaient purement et simplement
          quand la fenetre d'echange etait fermee, alors que l'impression reste toujours pertinente. */}
      <div className="msg-actions-bar">
        {printButton}
        {nextTabAction}
      </div>
    </>
  );
};

export default TabMessagerie;
